import Component from '@glimmer/component'
import { colors } from '@colors'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import { roundAccurately } from 'teacher-ui-client/utils/round'
import { PieChart, type TooltipConfig } from 'teacher-ui-client/components/giraffe/primitives/pie-chart'
import type { IntlService } from 'ember-intl'
import { service } from '@ember/service'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import type { BooksReadByGenreData } from 'teacher-ui-client/utils/student-events/fetchers/books-read-by-genre'

interface ChartData {
  series: {
    data: [string, number][]
    dataLabels: {
      enabled: boolean
    }
    showInLegend: boolean
    type: 'pie'
  }[]
  innerSize: string
  size: string
  subtitle: string
}

interface LegendData {
  fiction: {
    name: string
    percentage: number
  }
  nonfiction: {
    name: string
    percentage: number
  }
}

interface Args {
  data: BooksReadByGenreData
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class DashboardBooksReadByGenre extends Component<Signature> {
  @service intl!: IntlService

  get booksReadData(): [string, number][] {
    const { intl } = this

    const fictionLabel = intl.t('reporting.charts.dashboard.booksRead.byGenre.fictionLabel')
    const nonfictionLabel = intl.t('reporting.charts.dashboard.booksRead.byGenre.nonfictionLabel')
    const { fiction, nonfiction } = this.args.data

    return [
      [nonfictionLabel, nonfiction],
      [fictionLabel, fiction],
    ]
  }

  get colors(): string[] {
    return [colors.purplyPink300, colors.purplyPink400]
  }

  get chartData(): ChartData {
    const subtitle = formatNumber(this.totalCount, 1)
    const data = this.booksReadData
    const chartData = {
      series: [
        {
          data,
          dataLabels: { enabled: false },
          showInLegend: true,
          type: 'pie' as const,
        },
      ],
      innerSize: '80%',
      size: '80%',
      subtitle: `<h1>${subtitle}<h1>`,
    }
    return chartData
  }

  get totalCount(): number {
    const { fiction, nonfiction } = this.args.data
    return fiction + nonfiction
  }

  get legendDataPercentages(): LegendData {
    const total = this.totalCount
    const [[nonfictionName, nonfiction], [fictionName, fiction]] = this.booksReadData

    if (total === 0) {
      return {
        fiction: { name: fictionName, percentage: 0 },
        nonfiction: { name: nonfictionName, percentage: 0 },
      }
    }

    const fictionPercentage = (fiction / total) * 100
    const nonfictionPercentage = (nonfiction / total) * 100

    return {
      fiction: { name: fictionName, percentage: roundAccurately(fictionPercentage, 1) },
      nonfiction: { name: nonfictionName, percentage: roundAccurately(nonfictionPercentage, 1) },
    }
  }

  get tooltip(): TooltipConfig {
    return {
      enabled: true,
      backgroundColor: colors.neutral400,
      borderWidth: 0,
      borderRadius: 5,
      formatter: function (this: Highcharts.Point) {
        const formattedValue = formatNumber(this.y ?? 0, 2)
        return `<b>${this.name}</b>: ${formattedValue}`
      },
      shadow: false,
      style: { color: colors.white },
    }
  }

  <template>
    <div class="relative flex h-full flex-col pb-8" ...attributes>
      <PieChart
        @data={{this.chartData.series}}
        @subtitle={{this.chartData.subtitle}}
        @colors={{this.colors}}
        @size={{this.chartData.size}}
        @innerSize={{this.chartData.innerSize}}
        @tooltip={{this.tooltip}}
      />
      <div data-test-books-read-by-genre-legend class="absolute right-0 bottom-3 left-0 flex">
        <div data-test-books-read-by-genre-label="fiction" class="ml-[5%] flex flex-col items-center">
          <div class="bg-purply-pink-500 h-[10px] w-[10px] rounded-full"></div>
          <div
            data-test-books-read-by-genre-percentage
            class="text-dusty-black-500 text-3xl font-thin print:font-thin!"
          >
            {{formatValue this.legendDataPercentages.fiction.percentage append="%"}}
          </div>
          <div data-test-books-read-by-genre-name class="text-dusty-black-500 m-0 text-base font-bold print:font-bold!">
            {{this.legendDataPercentages.fiction.name}}
          </div>
        </div>
        <div data-test-books-read-by-genre-label="nonfiction" class="mr-[5%] ml-auto flex flex-col items-center">
          <div class="bg-purply-pink-300 h-[10px] w-[10px] rounded-full"></div>
          <div
            data-test-books-read-by-genre-percentage
            class="text-dusty-black-500 text-3xl font-thin print:font-thin!"
          >
            {{formatValue this.legendDataPercentages.nonfiction.percentage append="%"}}
          </div>
          <div data-test-books-read-by-genre-name class="text-dusty-black-500 m-0 text-base font-bold print:font-bold!">
            {{this.legendDataPercentages.nonfiction.name}}
          </div>
        </div>
      </div>
    </div>
  </template>
}

export default DashboardBooksReadByGenre
